import {
  Component,
  OnDestroy,
  OnInit,
  ViewChild
} from '@angular/core';

import { Subject, Subscription } from 'rxjs';

import { AppService } from 'src/app/app.service';
import { LoginapiService } from 'src/app/auth/loginapi.service';
import { ChatbotService } from '../websocket/chatbot.service';
import { takeUntil } from 'rxjs/operators';
interface ChatbotQuestion {
  title: string;
  question: string;
}

@Component({
  selector: 'app-copilot',
  templateUrl: './copilot.component.html',
  styleUrls: ['./copilot.component.scss']
})
export class CopilotComponent implements OnInit, OnDestroy {
  @ViewChild('commonChatbot')
  commonChatbot: any;
  // Response passed to common-chatbot
  chatBotResponse: any = null;
  destroy$ = new Subject<void>();
  // Current backend session
  sessionId = '';

  // API subscription
  private aiQuerySub: Subscription | null = null;
  private toolSub: Subscription | null = null;

  // WebSocket subscription
  private botResponseSub: Subscription | null = null;
  private fleetSub: Subscription | null = null;
  fleetList: any[] = [];
  welcomeMessage =
    `### Available Tools
- Get Active DTC Codes by Fleet
- Get Audit History by VINs
- Business Query Tool
- Get Aggregated Benchmark DTC Data
- Get Aggregated Benchmark Mileage
- Get Fleet Information
- Create Support Ticket
- Get DTC Descriptions
- Check VIN Eligibility
- Get VIN Enrollment Summary for All Accessible Fleets
- Get VIN Enrollment Summary for an Explicit Scope
- Get Inactive VINs
- Get Network Service Reports
- Get Vehicle Enrollment Details or Status Lists
- List All Failed Vehicles in a Fleet
- Get Signal Mappings
- Get Vehicle Specifications
- Detailed Network Service Report
- Signal GapAnalysis Report
`;
  exampleQuestions = [
    'Can you tell me the enrolment history of my vehicle?',
    'How do I change my CCS settings?',
    'How do I perform a Blue Button Key Press?',
    'What is the expected mileage I can get from my Ford F-150?',
    'What is the expected mileage I can get from my Chevy Silverado?',
    'What is the expected mileage I can get from my RAM 1500?',
    'What are all the fleets in my system?',
    'Is my vehicle eligible for enrolment?',
    'Is my list of vehicles eligible for enrolment?',
    'Can you give me a list of all active vehicles in all my fleets?',
    'Can you give me a list of all failed vehicles in all my fleets?',
    'Can you give me a list of pending vehicles in all my fleets?',
    'How many vehicles are there in my fleet?',
    'How many active vehicles are there in my fleet?',
    'How many failed vehicles are there in my fleet?',
    'How can I make my vehicle active?',
    'How many vehicles were enrolled in my fleet in the last week?',
    'How many vehicles were enrolled in my fleet in the last month?',
    'How many vehicles were enrolled in all my fleets in the last week?',
    'How many vehicles were enrolled in all my fleets in the last month?',
    'Can you tell me what vehicles have been inactive for the last 7 days?',
    'Can you tell me what vehicles have been inactive for the last month?',
    'Can you tell me my network status?',
    'Can you tell me if Ford is up?',
    'Can you tell me the status of my vehicle?',
    'Can you tell me the make, model and trim of my vehicle?'
  ];
  mentionGroups = [
    {
      key: 'fleet',
      label: 'Fleet',
      items: [
        { id: '1', name: 'Fleet A' },
        { id: '2', name: 'Fleet B' }
      ]
    }
    // ,
    // {
    //   key: 'vin',
    //   label: 'VIN',
    //   items: [
    //     { id: '1', name: '1FTFW1E50JFA12345' },
    //     { id: '2', name: '1GC4KYEY0KF123456' }
    //   ]
    // }
  ];
  historyId: any;
  chatHistory: any;
  selectedThreadHistory: any;
  constructor(
    private loginService: LoginapiService,
    private chatService: ChatbotService,
    private appService: AppService
  ) { }

  // ============================================================
  // INIT
  // ============================================================

  ngOnInit(): void {
    this.chatService.registerChatbot(this);
    this.subscribeToBotResponse();
    this.getFleetList();
    this.getToolList();
    this.getChatHistory()
  }

  selectChatHistory(event: any) {
    if (!event) {
      return;
    }
    const threadId = event?.thread_id || event?.threadId || event?.id || (typeof event === 'string' ? event : null);
    if (threadId) {
      sessionStorage.setItem('threadId', String(threadId));
    }
    if (event?.history_id || event?.historyId) {
      this.historyId = event.history_id || event.historyId;
    }
    const targetId = threadId || event?.id;
    if (targetId) {
      this.getSelectedHistory(String(targetId));
    }
  }

  getSelectedHistory(id: string) {
    this.appService.getHistoryById(id).pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: any) => {
          this.selectedThreadHistory = res;
          const threadId = res?.thread_id || res?.threadId || id;
          if (threadId) {
            sessionStorage.setItem('threadId', String(threadId));
          }
          if (res?.history_id || res?.historyId) {
            this.historyId = res.history_id || res.historyId;
          }
        },
        error: (err: any) => {
          console.error('Error fetching thread history:', err);
        }
      });
  }

  getChatHistory() {
    this.appService.getHistory().pipe(takeUntil(this.destroy$))
      .subscribe((res: any) => {
        this.chatHistory = res;
      })
  }

  openChatWithQuestion(
    question: string,
    title: string = ''
  ): void {

    if (!this.commonChatbot) {
      return;
    }

    this.commonChatbot.isOpen = true;

    this.commonChatbot.activeTab = 'chat';

    this.commonChatbot.askAI(
      title,
      question
    );
  }

  // ============================================================
  // RECEIVE QUESTION FROM COMMON CHATBOT
  // ============================================================

  onChatbotQuestion(
    event: ChatbotQuestion
  ): void {

    if (!event?.question?.trim()) {
      return;
    }

    const payload = {
      query: event.question.trim()
    };

    // Cancel only pending HTTP request if any.
    // Don't disconnect websocket here unnecessarily.
    if (this.aiQuerySub) {
      this.aiQuerySub.unsubscribe();
      this.aiQuerySub = null;
    }

    this.aiQuerySub = this.loginService
      .dataInsertionFt(payload)
      .subscribe({

        next: (res: any) => {

          if (!res?.session_id) {
            this.sendErrorToChatbot(
              'Unable to create chatbot session.'
            );

            return;
          }

          this.sessionId = res.session_id;
          this.historyId = res.history_id || this.historyId;
          // Store thread id
          if (res?.thread_id) {
            sessionStorage.setItem('threadId', res.thread_id);
          }

          this.connectWebSocket();
        },

        error: (error: any) => {

          console.error(
            'Chatbot API error:',
            error
          );

          this.sendErrorToChatbot(
            'Unable to process your request. Please try again.'
          );
        }

      });
  }

  // ============================================================
  // SUBSCRIBE TO WEBSOCKET RESPONSE
  // ============================================================

  private subscribeToBotResponse(): void {

    // Prevent duplicate subscription
    if (this.botResponseSub) {
      return;
    }

    this.botResponseSub =
      this.chatService.botResponse$
        .subscribe((response: any) => {

          if (!response) {
            return;
          }

          /*
           * IMPORTANT:
           * Always create a new object reference.
           *
           * Common chatbot uses @Input botResponse
           * and ngOnChanges.
           */
          if (
            typeof response === 'object' &&
            response !== null
          ) {

            this.chatBotResponse = {
              ...response
            };

          } else {

            this.chatBotResponse = response;

          }

        });
  }

  // ============================================================
  // CONNECT WEBSOCKET
  // ============================================================

  private connectWebSocket(): void {

    if (!this.sessionId) {
      return;
    }

    /*
     * Avoid an old socket remaining active.
     *
     * If your ChatbotService.connect() already closes
     * the old socket internally, disconnect() here can
     * be removed.
     */
    this.chatService.disconnect();

    this.chatService.connect(
      this.sessionId,
      this.historyId
    );
  }

  newChatOpen() {
    this.historyId = null;
    this.sessionId = '';
    this.selectedThreadHistory = null;
    sessionStorage.removeItem('threshId');
    sessionStorage.removeItem('threadId');
    this.getChatHistory();
  }

  // ============================================================
  // CANCEL QUERY
  // ============================================================

  onChatbotCancel(): void {

    // Cancel HTTP request
    if (this.aiQuerySub) {

      this.aiQuerySub.unsubscribe();

      this.aiQuerySub = null;
    }

    // Stop websocket
    this.chatService.disconnect();

    // End backend session
    if (this.sessionId) {

      const currentSessionId =
        this.sessionId;

      this.loginService
        .copilotEndSession(currentSessionId)
        .subscribe({

          next: (res: any) => {

            // console.log(
            //   'Chatbot session cancelled:',
            //   res
            // );

          },

          error: (error: any) => {

            console.error(
              'Error cancelling chatbot session:',
              error
            );

          }

        });

    }

    this.sessionId = '';
  }

  // ============================================================
  // CHAT CLOSED
  // ============================================================

  onChatbotClose(): void {

    // Cancel pending API request
    if (this.aiQuerySub) {

      this.aiQuerySub.unsubscribe();

      this.aiQuerySub = null;
    }

    // Close websocket
    this.chatService.disconnect();

    if (!this.sessionId) {
      return;
    }

    const currentSessionId =
      this.sessionId;

    this.sessionId = '';

    this.loginService
      .copilotEndSession(currentSessionId)
      .subscribe({

        next: (res: any) => {

          // console.log(
          //   'Chatbot session closed:',
          //   res
          // );

        },

        error: (error: any) => {

          console.error(
            'Error closing chatbot session:',
            error
          );

        }

      });
  }

  // ============================================================
  // SEND API ERROR BACK TO COMMON CHATBOT
  // ============================================================

  private sendErrorToChatbot(
    message: string
  ): void {

    /*
     * Common-chatbot already understands:
     *
     * {
     *   detail: '...'
     * }
     *
     * Passing error in same format also stops
     * common chatbot loading state.
     */
    this.chatBotResponse = {
      detail: message
    };
  }

  // ============================================================
  // DESTROY
  // ============================================================

  ngOnDestroy(): void {

    if (this.aiQuerySub) {
      this.aiQuerySub.unsubscribe();
      this.aiQuerySub = null;
    }

    if (this.botResponseSub) {
      this.botResponseSub.unsubscribe();
      this.botResponseSub = null;
    }

    if (this.fleetSub) {
      this.fleetSub.unsubscribe();
      this.fleetSub = null;
    }

    if (this.toolSub) {
      this.toolSub.unsubscribe();
      this.toolSub = null;
    }

    this.destroy$.next();
    this.destroy$.complete();

    this.chatService.disconnect();

    this.chatService.registerChatbot(null);
  }

  // ============================================================
  // GET FLEET LIST FROM APP SERVICE
  // ============================================================

  getFleetList(): void {
    if (this.fleetSub) {
      this.fleetSub.unsubscribe();
    }

    this.fleetSub = this.appService.getFleetList().subscribe({
      next: (res: any) => {
        this.fleetList = res || [];
        if (Array.isArray(res)) {
          const fleetItems = res.map((fleet: any) => ({
            id: fleet.id || fleet.fleetId || '',
            name: fleet.fleetName || fleet.name || fleet.fleetId || fleet.id || ''
          }));

          const fleetGroup = this.mentionGroups.find(g => g.key === 'fleet');
          if (fleetGroup) {
            fleetGroup.items = fleetItems;
          }
        }
      },
      error: (err: any) => {
        console.error('Error fetching fleet list:', err);
      }
    });
  }

  // ============================================================
  // GET TOOL LIST FROM APP SERVICE DYNAMICALLY
  // ============================================================

  getToolList(): void {
    if (this.toolSub) {
      this.toolSub.unsubscribe();
    }

    this.toolSub = this.appService.getToolList().subscribe({
      next: (res: any) => {
        let tools: string[] = [];

        if (res && res.fleetready && typeof res.fleetready === 'object') {
          tools = Object.values(res.fleetready) as string[];
        } else if (Array.isArray(res)) {
          tools = res.map((t: any) => typeof t === 'string' ? t : (t.name || t.tool_name || t.title || t.toolName || ''));
        } else if (res && typeof res === 'object') {
          for (const key of Object.keys(res)) {
            const val = res[key];
            if (Array.isArray(val)) {
              tools = val.map((t: any) => typeof t === 'string' ? t : (t.name || t.tool_name || t.title || t.toolName || ''));
              if (tools.length > 0) break;
            } else if (typeof val === 'object' && val !== null) {
              tools = Object.values(val).filter((item: any) => typeof item === 'string') as string[];
              if (tools.length > 0) break;
            }
          }
        }

        tools = tools.filter(t => typeof t === 'string' && t.trim() !== '');

        if (tools.length > 0) {
          this.welcomeMessage = `### Available Tools\n` + tools.map(t => `  - ${t}`).join('\n') + '\n';
        }
      },
      error: (err: any) => {
        console.error('Error fetching chatbot tools:', err);
      }
    });
  }

}